/**
 * Spotlight — the soft backdrop that focuses the customer's eye on the
 * target zone when a tour bubble is open.
 *
 * Design intent:
 *   • NOT a hard black mask (corporate SaaS look) — we use a soft
 *     radial gradient that brightens the target and gently dims the
 *     rest with an 8px backdrop blur.
 *   • A warm rosegold glow ring breathes around the highlighted zone
 *     so the customer's eye lands there immediately.
 *   • Tapping the dimmed area dismisses the bubble (familiar mobile
 *     gesture).
 *   • Smooth re-anchor between auto-walk steps — when the open beacon
 *     changes, the ring slides to the new target instead of flashing.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import useTour from "./useTour";

function findTarget(beaconId) {
    if (!beaconId || typeof document === "undefined") return null;
    if (beaconId === "__celebration__") return null;
    // The beacon's button has data-tour-target. We anchor the spotlight
    // on its PARENT (the zone the beacon describes), not the beacon
    // itself — so the spotlight ring frames the whole feature.
    const beacon = document.querySelector(`[data-tour-target="${beaconId}"]`);
    if (!beacon) return null;
    return beacon.parentElement || beacon;
}

export default function Spotlight() {
    const { openBeaconId } = useTour();
    const [rect, setRect] = useState(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!openBeaconId) {
            setVisible(false);
            // Delay the rect clear so the exit animation can play.
            const t = setTimeout(() => setRect(null), 320);
            return () => clearTimeout(t);
        }
        const t = findTarget(openBeaconId);
        if (!t) {
            // Celebration moment has no target — center-screen bubble.
            setRect(null);
            setVisible(true);
            return undefined;
        }
        // Smoothly bring the target into view before measuring.
        try {
            t.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        } catch { /* ignore */ }
        // Wait one frame for the scroll, then measure.
        const measure = () => {
            const r = t.getBoundingClientRect();
            setRect({
                top: r.top, left: r.left, width: r.width, height: r.height,
                centerX: r.left + r.width / 2, centerY: r.top + r.height / 2,
            });
            setVisible(true);
        };
        const r1 = requestAnimationFrame(() => requestAnimationFrame(measure));
        // Re-measure on scroll/resize so the ring tracks the element.
        window.addEventListener("scroll", measure, true);
        window.addEventListener("resize", measure);
        return () => {
            cancelAnimationFrame(r1);
            window.removeEventListener("scroll", measure, true);
            window.removeEventListener("resize", measure);
        };
    }, [openBeaconId]);

    if (!openBeaconId || typeof document === "undefined") return null;

    // Celebration moment — full-screen soft wash, no target ring.
    if (openBeaconId === "__celebration__" || !rect) {
        return createPortal(
            <div
                aria-hidden="true"
                className="jns-tour-spotlight jns-tour-spotlight-full"
                data-tour-visible={visible ? "true" : "false"}
                style={{ pointerEvents: "none" }}
            >
                <style>{`
                    .jns-tour-spotlight-full {
                        position: fixed; inset: 0; z-index: 70;
                        background: radial-gradient(circle at center,
                            rgba(255,232,200,0.18) 0%,
                            rgba(60,30,40,0.55) 60%,
                            rgba(40,15,25,0.72) 100%);
                        backdrop-filter: blur(8px);
                        -webkit-backdrop-filter: blur(8px);
                        opacity: 0; transition: opacity 300ms ease-out;
                    }
                    .jns-tour-spotlight-full[data-tour-visible="true"] { opacity: 1; }
                `}</style>
            </div>,
            document.body,
        );
    }

    // Pad the highlight ring a little so it breathes around the zone.
    const pad = 10;
    const ringTop = rect.top - pad;
    const ringLeft = rect.left - pad;
    const ringWidth = rect.width + pad * 2;
    const ringHeight = rect.height + pad * 2;
    const radius = Math.max(ringWidth, ringHeight) * 0.62;

    return createPortal(
        <div
            aria-hidden="true"
            className="jns-tour-spotlight"
            data-tour-visible={visible ? "true" : "false"}
            data-testid="tour-spotlight"
        >
            {/* Radial gradient mask — clear at the target, dim around it. */}
            <div
                className="jns-tour-spotlight-mask"
                style={{
                    background: `radial-gradient(circle at ${rect.centerX}px ${rect.centerY}px,
                        rgba(40,15,25,0) 0px,
                        rgba(40,15,25,0) ${radius * 0.55}px,
                        rgba(40,15,25,0.32) ${radius * 0.85}px,
                        rgba(40,15,25,0.62) ${radius * 1.4}px,
                        rgba(40,15,25,0.75) 100%)`,
                }}
            />
            {/* Soft warm glow ring around the target. */}
            <div
                className="jns-tour-spotlight-ring"
                style={{
                    top: ringTop, left: ringLeft,
                    width: ringWidth, height: ringHeight,
                }}
            />
            <style>{`
                .jns-tour-spotlight {
                    position: fixed; inset: 0; z-index: 70;
                    pointer-events: none;
                    opacity: 0; transition: opacity 300ms ease-out;
                }
                .jns-tour-spotlight[data-tour-visible="true"] { opacity: 1; }
                .jns-tour-spotlight-mask {
                    position: absolute; inset: 0;
                    backdrop-filter: blur(0px);
                    -webkit-backdrop-filter: blur(0px);
                }
                .jns-tour-spotlight-ring {
                    position: absolute; border-radius: 18px;
                    box-shadow:
                        0 0 0 1.5px rgba(232,160,99,0.65),
                        0 0 0 4px rgba(232,160,99,0.20),
                        0 16px 48px -16px rgba(180,80,60,0.55);
                    background: rgba(255,248,238,0.04);
                    transition: top 380ms cubic-bezier(0.34, 1.56, 0.64, 1),
                                left 380ms cubic-bezier(0.34, 1.56, 0.64, 1),
                                width 380ms cubic-bezier(0.34, 1.56, 0.64, 1),
                                height 380ms cubic-bezier(0.34, 1.56, 0.64, 1);
                    animation: jnsTourRingBreath 2.6s ease-in-out infinite;
                }
                @keyframes jnsTourRingBreath {
                    0%, 100% { box-shadow: 0 0 0 1.5px rgba(232,160,99,0.6),
                                           0 0 0 4px rgba(232,160,99,0.15),
                                           0 16px 48px -16px rgba(180,80,60,0.5); }
                    50%      { box-shadow: 0 0 0 1.5px rgba(232,160,99,0.85),
                                           0 0 0 7px rgba(232,160,99,0.25),
                                           0 20px 60px -16px rgba(180,80,60,0.65); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .jns-tour-spotlight-ring { animation: none !important; transition: none !important; }
                }
            `}</style>
        </div>,
        document.body,
    );
}
